import PropTypes from 'prop-types';
import { useContext, useEffect, useRef } from 'react';
import { PriceContext } from '../contexts/PriceContext';
import { UserContext } from '../contexts/UserContext';
import { useUnits } from '../hooks/useUnits';
import { gtmEventChangeUnits, gtmEventShowFeatures } from '../utils/analytics';
import { getCostsByPlanAndUnits, getLocalizedPrice } from '../utils/costs';
import { getUnitSelectOptions } from '../utils/plan';
import Plan from './Plan';

export default function Overview({ id, data, generalData, getLabel, onPlanSelect, paymentInterval }) {
	const { useDecimalPlaces } = useContext(PriceContext);
	const { currency, locale } = useContext(UserContext);
	const [units, setUnits] = useUnits(data.units);
	const prevUnits = useRef(units);

	useEffect(() => {
		if (prevUnits.current !== units) {
			gtmEventChangeUnits({ plan: id, units });
		}
		prevUnits.current = units;
	}, [units]);

	const prices = getCostsByPlanAndUnits(data, units, paymentInterval, useDecimalPlaces);

	let paymentHintLabel = getLabel('payment_hint_monthly');
	if (paymentInterval === 'annually') {
		const annualPrice = getLocalizedPrice(prices.annually.total, currency, locale);
		paymentHintLabel = getLabel('payment_hint_annually').replace('%s', annualPrice);
	}

	const getPricePerUnit = () => {
		if (generalData.show_price_per_unit) {
			const price = getLocalizedPrice(prices.monthly.perUnit, currency, locale);
			return getLabel('plan_price_per_unit').replace('%s', price);
		} else {
			return null;
		}
	};

	const unitSelect = {
		name: encodeURIComponent('select-plan-' + data.name).toLocaleLowerCase(),
		label: getLabel('plan_unit_selection'),
		onChange: (units) => setUnits(parseInt(units)),
		options: getUnitSelectOptions(data),
		value: String(units),
	};

	return (
		<Plan
			bottomLink={{ href: generalData.feature_page, label: getLabel('plan_all_features'), onClick: () => gtmEventShowFeatures(id) }}
			buttonLabel={getLabel('plan_primary_button')}
			description={data.description}
			featureListItems={data.features}
			highlight={data.most_popular ? getLabel('plan_most_popular') : ''}
			onButtonClick={() => onPlanSelect(id, data, paymentInterval, units)}
			price={getLocalizedPrice(prices.monthly.total, currency, locale)}
			priceExplanation={paymentHintLabel}
			priceInterval={`/ ${getLabel('payment_month')}`}
			pricePerUnit={getPricePerUnit()}
			unitSelect={unitSelect}
			featureListHeading={getLabel('plan_feature_list_heading')}
		/>
	);
}

Overview.propTypes = {
	data: PropTypes.object.isRequired,
	generalData: PropTypes.object.isRequired,
	getLabel: PropTypes.func.isRequired,
	id: PropTypes.string.isRequired,
	onPlanSelect: PropTypes.func.isRequired,
	paymentInterval: PropTypes.string.isRequired,
};
